import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";

const NAV = [
  { label: "Inicio", href: "/#inicio" },
  { label: "Servicios", href: "/#servicios" },
  { label: "Cómo funciona", href: "/#como-funciona" },
  { label: "Preguntas frecuentes", href: "/#faq" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/70 soft-shadow" : "bg-transparent"}`}>
      <nav className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <button onClick={() => go("/#inicio")} data-testid="nav-logo-btn">
          <Logo />
        </button>

        <div className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`nav-${n.href.replace("/#", "")}`}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors rounded-lg"
            >
              {n.label}
            </button>
          ))}
          <Link to="/consultar-pedido" data-testid="nav-track" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors rounded-lg">
            Consultar pedido
          </Link>
          <Link to="/soporte" data-testid="nav-support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors rounded-lg">
            Soporte
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="theme-toggle" aria-label="Cambiar tema" className="p-2 rounded-lg text-slate-600 hover:text-indigo-600 hover:bg-slate-100 transition-colors">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <Button
            onClick={() => go("/#servicios")}
            data-testid="nav-comprar-btn"
            className="hidden sm:inline-flex rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 shadow-lg shadow-indigo-500/25 active:scale-95 transition-all"
          >
            Comprar ahora
          </Button>
          <button className="md:hidden p-2 text-slate-800" onClick={() => setOpen(!open)} data-testid="nav-hamburger">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-100 px-5 py-4 space-y-1 soft-shadow" data-testid="mobile-menu">
          {NAV.map((n) => (
            <button key={n.href} onClick={() => go(n.href)} className="block w-full text-left px-3 py-3 text-base font-medium text-slate-700 rounded-lg hover:bg-slate-50">
              {n.label}
            </button>
          ))}
          <Link to="/consultar-pedido" onClick={() => setOpen(false)} className="block px-3 py-3 text-base font-medium text-slate-700 rounded-lg hover:bg-slate-50">
            Consultar pedido
          </Link>
          <Link to="/soporte" onClick={() => setOpen(false)} className="block px-3 py-3 text-base font-medium text-slate-700 rounded-lg hover:bg-slate-50">
            Soporte
          </Link>
          <Button onClick={() => go("/#servicios")} className="w-full mt-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold h-12">
            Comprar ahora
          </Button>
        </div>
      )}
    </header>
  );
};
